import React from "react";

const Section = ({ title, children }) => (
    <section className="mb-12" data-testid={`legal-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        <h2 className="un-h3 text-2xl md:text-3xl mb-4 text-un-berry">{title}</h2>
        <div className="text-un-onyx/85 space-y-4 text-base leading-relaxed max-w-3xl">
            {children}
        </div>
    </section>
);

const Legal = () => {
    const lastUpdated = "February 4, 2026";
    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="legal-page">
            <div className="mb-12">
                <div className="un-label text-un-grape mb-3">LEGAL</div>
                <h1 className="un-h1 text-5xl md:text-6xl">
                    Terms &amp; <span className="text-un-berry">Privacy</span>.
                </h1>
                <p className="un-mono text-xs text-un-onyx/60 mt-4">
                    LAST UPDATED: {lastUpdated.toUpperCase()}
                </p>
            </div>

            <div className="un-card p-8 md:p-12 mb-10 bg-un-lime/40 border-un-onyx">
                <p className="text-sm text-un-onyx/85 leading-relaxed">
                    <strong>Quick summary:</strong> The UnCloned® Short Form Video Strategist is a private tool for
                    invited clients only. We keep your intake answers and generated scripts private, we do not train
                    third-party models on your content, and we never sell your data. Access is granted through an
                    activation code and can be revoked or expire based on your plan.
                </p>
            </div>

            <Section title="Who we are">
                <p>
                    This application is operated by <strong>UnCloned® Media</strong> (also referred to as
                    &ldquo;UnCloned,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), a brand of
                    UnCloned® Marketing. For any legal or data inquiries, contact
                    {" "}<a className="text-un-berry underline" href="mailto:info@unclonedmarketing.com">
                        info@unclonedmarketing.com
                    </a>.
                </p>
            </Section>

            <Section title="Eligibility & Access">
                <p>
                    Access to the UnCloned® Short Form Video Strategist is limited to individuals who have received a
                    valid activation code from an authorized UnCloned® admin. You may not resell, share, or transfer
                    your login credentials without written permission. Access may be time-limited based on the
                    expiration date attached to your activation code or account.
                </p>
                <p>
                    We reserve the right to revoke access at any time for misuse, breach of these terms, or expiration
                    of the underlying agreement.
                </p>
            </Section>

            <Section title="Your Content">
                <p>
                    You retain full ownership of the intake responses you provide and the video scripts generated for
                    you. Scripts are yours to use, edit, publish, and monetize. We do not claim rights to your
                    generated output.
                </p>
                <p>
                    You are responsible for the accuracy of any information you input and for how you use the scripts
                    you produce. Generated scripts should be reviewed for tone, factual accuracy, and legal compliance
                    before publishing.
                </p>
            </Section>

            <Section title="How the AI Works">
                <p>
                    Scripts are generated by third-party large language models under a master strategist prompt
                    maintained by UnCloned®. Your intake responses are sent to the model in order to generate output.
                    Model providers do not use inputs sent through our platform to train their public models under the
                    terms of our data-processing agreements with them.
                </p>
                <p>
                    The AI is a creative tool. It may occasionally produce output that requires editing, tone
                    adjustment, or fact-checking before publication. Always verify claims, statistics, and personal
                    references before posting anything publicly.
                </p>
            </Section>

            <Section title="Privacy & Data">
                <p>We store the following information to operate the service:</p>
                <ul className="list-disc pl-6 space-y-1">
                    <li>Your account details: name, email, and a securely hashed password.</li>
                    <li>Your intake answers and generated scripts, associated with your account.</li>
                    <li>Basic access logs (login events, generation events) for security and support.</li>
                </ul>
                <p>
                    We do not sell your data. We do not share your intake responses or generated scripts with anyone
                    outside of UnCloned® admins responsible for supporting your account. We do not use your content to
                    train AI models. You can request export or deletion of your data at any time by emailing us.
                </p>
            </Section>

            <Section title="Security">
                <p>
                    Passwords are hashed using industry-standard algorithms. Authentication uses signed session
                    tokens with expiration. All traffic between your browser and our servers is encrypted in transit.
                    Despite reasonable precautions, no online service is 100% secure. Report any suspected security
                    issue to us immediately.
                </p>
            </Section>

            <Section title="Acceptable Use">
                <p>You agree not to:</p>
                <ul className="list-disc pl-6 space-y-1">
                    <li>Use the service to create content that is unlawful, defamatory, harassing, or fraudulent.</li>
                    <li>Attempt to reverse-engineer, scrape, or extract the master strategist prompt.</li>
                    <li>Use the platform on behalf of another person or business without authorization.</li>
                    <li>Automate use in ways that overload or interfere with normal service operation.</li>
                </ul>
            </Section>

            <Section title="Payments & Refunds">
                <p>
                    Access is granted through the plan or agreement under which your activation code was issued.
                    Refunds, renewals, and cancellations follow the terms of that underlying agreement. If you
                    purchased directly from UnCloned® Marketing, refer to your original purchase terms.
                </p>
            </Section>

            <Section title="Warranty Disclaimer">
                <p>
                    The service is provided &ldquo;as is&rdquo; without warranties of any kind, express or implied.
                    We do not guarantee that generated scripts will produce any specific business, marketing, or
                    revenue outcome. Results depend on how the scripts are used, executed, and delivered.
                </p>
            </Section>

            <Section title="Limitation of Liability">
                <p>
                    To the maximum extent permitted by law, UnCloned® Media, its officers, employees, and affiliates
                    will not be liable for indirect, incidental, or consequential damages arising from your use of
                    the service. Total liability for direct damages is limited to the amount paid for access to the
                    service in the twelve months preceding the claim.
                </p>
            </Section>

            <Section title="Changes to These Terms">
                <p>
                    We may update these Terms &amp; Privacy from time to time. Material changes will be communicated
                    to active users by email or an in-app notice. Continued use of the service after changes take
                    effect constitutes acceptance of the updated terms.
                </p>
            </Section>

            <Section title="Contact">
                <p>
                    Questions, data requests, or legal notices should be directed to
                    {" "}<a className="text-un-berry underline" href="mailto:info@unclonedmarketing.com">
                        info@unclonedmarketing.com
                    </a>.
                </p>
            </Section>

            <div className="un-divider pt-8 mt-8 text-xs text-un-onyx/50 un-mono">
                COPYRIGHT UNCLONED MEDIA 2026 · ALL RIGHTS RESERVED
            </div>
        </div>
    );
};

export default Legal;
